import { createElement, type ReactNode } from 'react'
import { Button, HStack, Menu, Portal } from '@chakra-ui/react'
import { FiChevronDown } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { CallButton } from '../../../shared/CallButton'
import { useUpdateFollowUpItem } from '../../../../hooks/useFollowUp'
import { extractApiMessage } from '../../../../lib/apiError'
import type {
  FollowUpItemRowDto, FollowUpKindDto, FollowUpStatus,
} from '../../../../types/followUp'

const OUTCOMES: Array<{ status: FollowUpStatus; label: string }> = [
  { status: 'Done', label: 'Done' },
  { status: 'Couldnt', label: "Couldn't reach" },
  { status: 'Declined', label: 'Declined' },
  { status: 'Cancelled', label: 'Cancel' },
]

function CallRowActions({ campaignId, row }: { campaignId: string; row: FollowUpItemRowDto }) {
  const { mutate: updateItem, isPending } = useUpdateFollowUpItem(campaignId)

  // Item status only, via the P1 PATCH: marking an outcome here never records a visit or a call.
  const mark = (status: FollowUpStatus) => {
    updateItem({ itemId: row.id, status }, {
      onError: (err) => toaster.create({
        title: extractApiMessage(err, 'Could not update the follow-up.'), type: 'error', duration: 5000,
      }),
    })
  }

  return (
    <HStack gap={2} flexWrap="wrap">
      {/* Closed items (Done, Declined, ...) are not called; a Do Not Contact person is never dialed. */}
      {row.status === 'Open' && (
        <CallButton phone={row.phone} disabledReason={row.doNotContact ? 'Do not contact' : undefined} />
      )}
      {row.status === 'Open' && (
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <Button size="sm" variant="outline" minH="44px" loading={isPending}
              onClick={(e) => e.stopPropagation()}>
              Mark outcome <FiChevronDown />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content onClick={(e) => e.stopPropagation()}>
                {OUTCOMES.map(o => (
                  <Menu.Item key={o.status} value={o.status} onSelect={() => mark(o.status)}>
                    {o.label}
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      )}
    </HStack>
  )
}

/** Value for FollowUpTab's `rowActions`: Call + Mark outcome on call-mode rows, nothing on route rows. */
export function buildCallRowActions(campaignId: string, kinds: FollowUpKindDto[]) {
  return (row: FollowUpItemRowDto): ReactNode => {
    const isCall = row.kind === 'CallBack'
      || (row.kind === 'Custom' && kinds.find(k => k.id === row.customKindId)?.mode === 'Call')
    if (!isCall) return null
    return createElement(CallRowActions, { campaignId, row })
  }
}
